// Static site content for EnviroJustice PR (nav, features, FAQs, team).
// Dynamic data (stories, contacts, newsletter) is served by the backend API.

export const SITE = {
  name: "EnviroJustice PR",
  tagline: "Preserving the earth for future generations",
  email: "team@envirojusticepr.com",
  location: "San Juan, Puerto Rico",
  facebook: "https://www.facebook.com/groups/envirojusticepr",
  logo: "/img/logo.png",
};

export const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Team", path: "/team" },
  { label: "Tell Your Story", path: "/tell-your-story" },
  { label: "Discover Stories", path: "/stories" },
];

export const IMAGES = {
  heroMain: "/media/stock/recycling-and-waste-management-4.jpg",
  heroSmall: "/img/hero-small.png",
  about: "/img/about.jpg",
  banner: "/img/banner.jpg",
};

export const FEATURES = [
  {
    id: 1,
    title: "Air Quality",
    icon: "/img/feature-air.svg",
    text: "Contribute to a shared community effort by reporting your observations on air quality.",
  },
  {
    id: 2,
    title: "Waste Management",
    icon: "/img/feature-waste.svg",
    text: "Discover sustainable waste management solutions and successful stories for a cleaner, greener future.",
  },
  {
    id: 3,
    title: "Pollution",
    icon: "/img/feature-pollution.svg",
    text: "Fight pollution and explore impactful initiatives and join the movement towards a cleaner, healthier environment.",
  },
  {
    id: 4,
    title: "Water Contamination",
    icon: "/img/feature-water.svg",
    text: "Learn about its causes, effects, and how activists contribute to preserving our precious water resources.",
  },
  {
    id: 5,
    title: "Violation Prevention",
    icon: "/img/feature-violation.svg",
    text: "Learn how you can contribute to a sustainable and ecologically responsible community.",
  },
  {
    id: 6,
    title: "Sustainable Energy",
    icon: "/img/feature-energy.svg",
    text: "Dive into inspiring narratives that showcase the positive impact of sustainable energy solutions.",
  },
];

export const STATS = [
  { id: 1, value: 400, suffix: "+", label: "Submitted Stories", icon: "HeartHandshake" },
  { id: 2, value: 20, suffix: "k+", label: "Community Members", icon: "Users" },
  { id: 3, value: 4, suffix: "+", label: "Media Recognition", icon: "Newspaper" },
  { id: 4, value: 50, suffix: "+", label: "Community Projects", icon: "Sprout" },
];

export const FAQS = [
  {
    id: 1,
    q: "What is EnviroJusticePR?",
    a: "EnviroJusticePR is a youth-led 501(c)3 environmental conservation organization from Puerto Rico. We showcase real, positive stories of individuals, communities and organizations making a difference, to inspire a global audience to take meaningful actions towards a greener and more sustainable future.",
  },
  {
    id: 2,
    q: "How do I submit my story?",
    a: "Use the Tell Your Story form on this website. Provide your author details, a title, your story, a featured image and a category. Our editorial team reviews every submission before it is published.",
  },
  {
    id: 3,
    q: "What kinds of stories are eligible for submission?",
    a: "We welcome a diverse range of success stories in the field of ecology. Share stories related to environmental initiatives, sustainable practices, conservation efforts, and positive impacts on nature. Whether it's a community-led project, individual achievements, or corporate initiatives, we're eager to hear and showcase positive ecological contributions.",
  },
  {
    id: 4,
    q: "What happens after I submit?",
    a: "Your story is saved securely and marked as pending. Once our editorial team approves it, it appears in Discover Stories and may be shared on our social media channels to inspire others. We will notify you by email when it is live.",
  },
];

export const CATEGORIES = [
  "Air & Water Pollution",
  "Biodiversity",
  "General Ecosystem",
  "Global Warming & Climate Change",
  "Industrial Pollution",
  "Invasive Species",
  "Recycling & Waste Management",
  "Resource Depletion",
  "Urbanization",
];

export const TEAM = [
  {
    id: 1,
    group: "Leadership",
    name: "Sasha Fischer",
    role: "Co-Founder and President of EnviroJustice PR organization",
    bio: [
      "Sasha is a rising senior at a New York high school with a strong interest in environmental sustainability and water access. Her interest began in middle school, when her robotics team developed a machine to remove plastic debris from the Hudson River.",
      "She later researched global food supply chains and regional food and water scarcity through the New York Federal Reserve High School Challenge. Sasha also co-founded her school's chapter of the Thirst Project, which works to expand access to safe, clean water, and she participates in national and international science competitions.",
    ],
    image: "/team/sasha-fischer.jpg",
  },
  {
    id: 2,
    group: "Leadership",
    name: "Cora Loomis",
    role: "Co-Founder and Vice-President of EnviroJustice PR organization",
    bio: [
      "Cora Loomis is a rising junior at a Massachusetts boarding school. Having attended middle school in Puerto Rico, she is interested in how economic and environmental factors shape Hispanic communities. At school, Cora leads 15 students in financial-literacy learning and investing. She also raised $5,000 for a transitional home in Puerto Rico, reflecting her commitment to underserved communities. Cora is actively involved in an environmental economics research project, with a plan to better understand how economic and environmental challenges affect Hispanic communities.",
    ],
    image: "/team/cora-loomis.jpg",
  },
  {
    id: 3,
    group: "Advisory Board",
    name: "Catherine Vasnetsov",
    role: "Advisory Board Member",
    bio: [
      "Catherine is a student in LSM, a dual-degree program at the Wharton business school. Originally from Puerto Rico, she co-founded EnviroJusticePR, a youth-led 501(c)3 environmental conservation organization. In recognition of her leadership in community-driven restoration projects, she was honored with the 2024 U.S. President's Environmental Youth Award (PEYA).",
      "Her academic pursuits include research in computational biochemistry and applying machine learning to study malaria and viruses, with findings presented at several international conferences. Catherine has also combined creativity and advocacy by designing hand-painted coasters to fundraise for young students in immersive biology camp programs.",
    ],
    image: "/team/catherine-vasnetsov.jpg",
  },
  {
    id: 4,
    group: "Advisory Board",
    name: "Isabella Muradyan",
    role: "Advisory Board Member",
    bio: [
      "Isabella is pursuing her passion for biology and medicine at USC, blending her academic interests with a deep love for the ocean. An avid snorkeler and surfer, she channels her connection to the sea into her commitment to ecological conservation. Her work focuses on community-based strategies that drive tangible, local impact. As co-founder of the Aqua Azure project, Isabella has led fundraising initiatives and overseen the installation of filtered water fountains in multiple Puerto Rico schools, improving access to clean water for hundreds of students and teachers.",
    ],
    image: "/team/isabella-muradyan.jpg?v=2",
  },
  {
    id: 5,
    group: "Advisory Board",
    name: "Victor Vasnetsov",
    role: "Advisory Board Member",
    bio: [
      "Victor combines his passions for the environment, economics, and entrepreneurship to create a better world for future generations. His community leadership in promoting environmental awareness earned him the prestigious Coca-Cola Scholarship, one of the largest national merit-based awards. His research results on novel multi-action cancer drugs were published in several academic journals and received a Regeneron ISEF Grand Award in Chemistry. Now a student at Cornell's Dyson School of Business, Victor is majoring in finance and strategy to advance his mission of driving sustainable innovation.",
    ],
    image: "/team/victor-vasnetsov.jpg",
  },
];
